package com.advaita.cloud.drive.ui.store

import com.advaita.cloud.drive.shared.SourceStatus
import com.advaita.cloud.drive.ui.api.CreateTarget

/** One toolbar button that creates something: whether it works here, and its tip. */
data class CreateAction(val enabled: Boolean, val tip: String)

/** The five toolbar buttons that create something, as [createActions] decides them. */
data class CreateActions(
    val newFolder: CreateAction,
    val uploadFiles: CreateAction,
    val uploadFolder: CreateAction,
    val pull: CreateAction,
    val runWorkflow: CreateAction,
)

/** The top level of Shared with me: a list of roots, with no folder open to add to. */
const val SHARED_TOP_LEVEL_TIP = "Open a shared folder to add files to it."

/**
 * A shared folder where the caller's grants on the shared root hold no `upload`. It names the
 * root and claims nothing about the folder itself (the level premise, Tim 2026-09-28): a deeper
 * grant may exist that Shared with me does not list as its own root.
 */
fun sharedNoAddTip(rootName: String) = "Your share of $rootName does not include adding files."

/** A shared folder where the caller's grants on the shared root hold no `execute`. */
fun sharedNoRunTip(rootName: String) = "Your share of $rootName does not include running workflows."

/** A shared folder whose grants the last read did not give now (`unavailable`, `rejected`). */
const val SHARED_GRANTS_UNKNOWN_TIP = "Your access to this folder is not known yet. Open Shared with me again to check."

/** The grants read is `refused` for this session (the domain fallback): that does not pass. */
const val SHARED_GRANTS_REFUSED_TIP = "Adding files to shared folders is not available for your account."

/**
 * ADV-1330 (the shared-folder create slice, scope 2 and 9, Question 1 and 3 (a)): which create
 * buttons work for [target], and what each tip says. [capabilities] is the caller's grants on a
 * shared folder's root ([SharedWithMeStore.currentCapabilities], the union of every live grant),
 * and [rootName] is that root's name (`trail[0]`). My Drive ignores both. [grantsStatus] is
 * the last grants read's `sharesStatus` ([SharedWithMeStore.grantsStatus]). When it is not `ok`,
 * a disabled button does not call the person a viewer: `refused` (permanent for the session)
 * says [SHARED_GRANTS_REFUSED_TIP], and any other status [SHARED_GRANTS_UNKNOWN_TIP].
 *
 * - My Drive: every button works, with the tips as before (ADV-1330 F3 kept them there).
 * - No target (the top level of Shared with me): every button is off, with [SHARED_TOP_LEVEL_TIP].
 * - A shared folder: `upload` turns on the four create buttons, and `execute` turns on Run
 *   workflow. The tips are My Drive's, "into <folder>". Without the capability, the button is
 *   off with [sharedNoAddTip] or [sharedNoRunTip], which name the root.
 *
 * The busy states (the config not read yet, a pull submitting, the catalog loading) stay in the
 * Header, as before. The server stays the judge of each write. Example: Lena is an Editor on Run
 * 42, so her Upload files tip reads "Upload one or more files into Run 42". Priya is a Viewer on
 * Project X, so inside Project X/raw hers reads "Your share of Project X does not include adding
 * files."
 */
fun createActions(
    target: CreateTarget?,
    capabilities: List<String>,
    grantsStatus: String? = SourceStatus.OK,
    rootName: String? = null,
): CreateActions = when (target) {
    is CreateTarget.MyDrive -> CreateActions(
        newFolder = CreateAction(true, "Create a new folder in the current directory"),
        uploadFiles = CreateAction(true, "Upload one or more files to the current directory"),
        uploadFolder = CreateAction(true, "Upload an entire folder (preserves the folder structure)"),
        pull = CreateAction(
            true,
            "Download files from public URLs, or private S3 buckets using your own AWS keys, into the current folder",
        ),
        runWorkflow = CreateAction(true, "Choose a curated workflow and pick the files to run it on"),
    )
    null -> CreateAction(false, SHARED_TOP_LEVEL_TIP).let { off -> CreateActions(off, off, off, off, off) }
    is CreateTarget.SharedFolder -> {
        val upload = "upload" in capabilities
        val execute = "execute" in capabilities
        val name = target.name
        fun off(tip: String) = CreateAction(
            false,
            when (grantsStatus) {
                SourceStatus.OK -> tip
                SourceStatus.REFUSED -> SHARED_GRANTS_REFUSED_TIP
                else -> SHARED_GRANTS_UNKNOWN_TIP
            },
        )
        val root = rootName ?: name
        fun add(tip: String) = if (upload) CreateAction(true, tip) else off(sharedNoAddTip(root))
        CreateActions(
            newFolder = add("Create a new folder in $name"),
            uploadFiles = add("Upload one or more files into $name"),
            uploadFolder = add("Upload an entire folder into $name (preserves the folder structure)"),
            pull = add("Download files from public URLs, or private S3 buckets using your own AWS keys, into $name"),
            runWorkflow = if (execute) CreateAction(true, "Choose a curated workflow and run it on files in $name")
            else off(sharedNoRunTip(root)),
        )
    }
}
